import { createBrowserRouter, Navigate, type RouteObject } from "react-router-dom";
import { RequireAuth } from "@web/app/RequireAuth";
import { PageTitleRoot, type RouteTitleHandle } from "@web/app/pageTitle";
import { NotFoundPage } from "@web/pages/NotFoundPage";
import { AuthCallbackPage } from "@web/pages/AuthCallbackPage";
import { RequirePlatformAdmin } from "./RequirePlatformAdmin";
import { PlatformLayout } from "./PlatformLayout";
import { PlatformLoginPage } from "../pages/PlatformLoginPage";
import { TenantsPage } from "../pages/TenantsPage";
import { TenantPage } from "../pages/TenantPage";
import { PlatformSettingsPage } from "../pages/PlatformSettingsPage";

/** A route's standing title, as an i18n key. Typed so a misspelt property name is a build error
 *  rather than a silently title-less bar. */
const titled = (titleKey: string): RouteTitleHandle => ({ titleKey });

// Route table of the operator console (ADR-0014 §6 phase 2, task C-1).
//
// THE PATHS ARE HOST-RELATIVE, not `/platform/…`. In apps/web these pages lived under a `platform/`
// prefix because they shared the product's origin with everything else and the prefix was the only
// thing saying whose surface they were. Here the HOST says it, and `platform.<domain>/platform/tenants`
// would say it twice.
//
// Every route under the shell carries BOTH guards: `RequireAuth` for a session at all and
// `RequirePlatformAdmin` for PLATFORM_ADMIN. The role check is not redundant with the host — a host is
// not an authorization boundary, and any authenticated person can ask this origin for this path. It is
// still only the UI's half: the API enforces its own (C-2 adds the client check to the server side,
// which is the half that cannot be bypassed).
//
// PAGE TITLES. A route under the shell names itself with `handle: { titleKey }` and the shell renders it
// as the top bar's <h1> (see @web/app/pageTitle for the mechanism). The login route carries one too: the
// same channel writes the BROWSER TAB, which every route has, shell or not.
export const routes: RouteObject[] = [
  { path: "/login", element: <PlatformLoginPage />, handle: titled("auth.loginTitle") },
  // OIDC authorization-code + PKCE redirect target. Public so the `?code=` lands here and is exchanged
  // before RequireAuth can bounce it. The realm registers this host's `/auth/callback` for the
  // `clms-platform` client (CLMS_PLATFORM_REDIRECT_URIS).
  { path: "/auth/callback", element: <AuthCallbackPage />, handle: titled("auth.signingIn") },
  {
    path: "/",
    element: (
      <RequireAuth>
        <PlatformLayout />
      </RequireAuth>
    ),
    children: [
      // The console has no landing page of its own: the tenant list IS where an operator starts, so "/"
      // redirects to it rather than drawing a dashboard with one link on it. `replace` so Back does not
      // bounce between the two.
      { index: true, element: <Navigate to="/tenants" replace /> },
      // Cross-tenant tenant administration (US-1.6) with the platform-owned half of each tenant's
      // settings (ADR-0014 §7.3) and the pääkäyttäjyys handover. The tenant's own name and logo are
      // deliberately NOT here — they are the tenant's settings, which its own pääkäyttäjä edits in
      // apps/web.
      {
        path: "tenants",
        element: (
          <RequirePlatformAdmin>
            <TenantsPage />
          </RequirePlatformAdmin>
        ),
        handle: titled("tenants.title"),
      },
      // ONE tenant — every act the operator can perform on a customer, as sections of its own page
      // (handoff: docs/product/alustakonsoli/tenanttihallinta/design_handoff_tenant_page). The handle
      // names the standing title, which stands while the read is in flight; the page then overrides it
      // with the tenant's SLUG through the same channel (`usePageTitle`), because a bar reading "Alustan
      // hallinta" on four different customers' pages names the console rather than the page.
      {
        path: "tenants/:tenantId",
        element: (
          <RequirePlatformAdmin>
            <TenantPage />
          </RequirePlatformAdmin>
        ),
        handle: titled("tenants.title"),
      },
      // Kyselymoottori PLATFORM parameters (KM-E9 / US-E9.2b, spec luku 7.3 class "Alustataso"): the
      // factor library and the platform-wide k raise — the values the platform operator owns for every
      // tenant alike, and neither of them a form field (a k raise is a deployment decision, not a
      // request). The tenant's own parameters are a different page for a different audience, and stay in
      // apps/web: conflating them is how a tenant admin ends up looking at a platform switch.
      {
        path: "parameters",
        element: (
          <RequirePlatformAdmin>
            <PlatformSettingsPage />
          </RequirePlatformAdmin>
        ),
        handle: titled("questionnaires.settings.platformTitle"),
      },
      { path: "*", element: <NotFoundPage />, handle: titled("notFound.heading") },
    ],
  },
];

// `routes` is the table; the ROUTER hangs it all under one pathless route whose element is the page
// title channel (@web/app/pageTitle `PageTitleRoot`), so the title resolved from the matched handle
// reaches the browser tab on every route — the pre-session ones included — and the shell's <h1> below it.
export const router = createBrowserRouter([
  { element: <PageTitleRoot appNameKey="platform.brand" />, children: routes },
]);
